import { Card, CardContent, Box, Typography, Stack, Divider } from '@mui/material';
import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

interface SectionCardProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  /** Right-aligned header content (buttons, chips, etc.). */
  action?: ReactNode;
  children: ReactNode;
  /** Remove inner padding (e.g. when embedding a full-bleed table). */
  disableContentPadding?: boolean;
  sx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
}

/**
 * A titled content card with consistent spacing and a soft header divider.
 * Use to group related fields/lists instead of loose Paper blocks.
 */
const SectionCard = ({
  title,
  subtitle,
  icon,
  action,
  children,
  disableContentPadding,
  sx,
  contentSx,
}: SectionCardProps) => (
  <Card sx={{ overflow: 'hidden', ...sx }}>
    {(title || action) && (
      <>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1.5}
          sx={{ px: { xs: 2, sm: 2.5 }, py: 1.75 }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
            {icon && (
              <Box sx={{ color: 'primary.main', display: 'flex', '& svg': { fontSize: 22 } }}>{icon}</Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              {title && (
                <Typography variant="h6" noWrap>
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography variant="caption" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Stack>
          {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
        </Stack>
        <Divider />
      </>
    )}
    {disableContentPadding ? (
      <Box sx={contentSx}>{children}</Box>
    ) : (
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } }, ...contentSx }}>
        {children}
      </CardContent>
    )}
  </Card>
);

export default SectionCard;
